import * as global from 'brokertslib/src/helpers/global'
import { Stack, useDisclosure } from '@chakra-ui/react'
import { addDays, addMonths, format } from 'date-fns'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router'

import ErrorSummary from '@/components/ErrorSummary'
import { Steps } from '@/components/Steps/Steps'
import { SidebarProvider } from '@/context/SidebarContext'
import { StepsProvider } from '@/context/StepsContext'
import { WizzardHeaderProvider } from '@/context/WizzardHeaderContext'
import { stepsStock } from '@/data/steps'
import { useCheckActiveContractsForStock, useCheckStockVin, useCreateStock, useGetInsuranceParams, type CancelledContractItem, type ManualStornoContractsInfo } from '@/hooks/useWizard'
import { useSelections } from '@/hooks'
import { InsuranceParamsType, SelectionType } from 'BrokerTsLib/src/enums'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { PageWrapper } from '@/layouts/components/PageWrapper'
import { Sidebar } from '@/layouts/components/Sidebar'
import { Loading } from '@/components/Loading'
import { InitialVehicleValues, type InsuranceParamsLeasingDto, type VinOnlineVehicleDto } from 'BrokerTsLib/src/dtos'
import { Selection } from 'BrokerTsLib/src/enums'
import { ActiveContractsModal } from './ActiveContractsModal'
import { CancelledContractsModal } from './CancelledContractsModal'
import { CreateStockBody } from './CreateStockBody'
import type { FormValues } from './types'

export const StockPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Aktivní id skladoveho pojisteni – z URL (/leasing/stock/{id}) nebo programově nastavené
  // (resume po částečně vytvořeném, ale nesjednaném skladovem pojisteni – vytvoreni paramsu prošlo, sjednání ne).
  // Provider data pro toto id sám dočte a dál se pracuje jako s editací existujícího záznamu.
  const { insuranceParams: editParams, isLoadingData: isLoadingEdit, isContracted, insuranceParamsId, setInsuranceParamsId, triggerNewInsurance, clearTriggerNewInsurance } = useInsuranceParams()

  const isEdit = !!insuranceParamsId
  const origInsuranceParamsId = isEdit ? null : searchParams.get('origParamsId')

  const isFromPs = !!origInsuranceParamsId

  // PS režim: vlastní fetch dle query origParamsId
  const { data: psParams, isLoading: isLoadingPs } = useGetInsuranceParams(origInsuranceParamsId!, {
    enabled: isFromPs,
  })

  const ipl = (isEdit ? editParams : psParams?.data) as InsuranceParamsLeasingDto | undefined
  const isLoading = isEdit ? isLoadingEdit : isLoadingPs

  const offerId = ipl?.generalParams?.selectedOfferId

  const { data: druhData } = useSelections(SelectionType.DruhVozidla, { enabled: isFromPs })
  const { data: palivoData } = useSelections(SelectionType.Palivo, { enabled: isFromPs })
  const { data: puvodData } = useSelections(SelectionType.PuvodVozidla, { enabled: isFromPs })

  const psVehicle = useMemo<VinOnlineVehicleDto | null>(() => {
    const v = ipl?.vehicle
    if (!v) return null
    const regDate = v.firstRegistrationDate ? new Date(v.firstRegistrationDate) : undefined
    return {
      vin: v.vin ?? undefined,
      registrationMark: v.licensePlate ?? undefined,
      MakeBase: v.znacka ?? undefined,
      ModelText: v.model ?? v.zakladniModel ?? undefined,
      CategoryID: v.druh ?? undefined,
      druh: druhData?.data?.find(x => x.id === v.druh)?.description,
      FuelID: v.palivo ?? undefined,
      druhPaliva: palivoData?.data?.find(x => x.id === v.palivo)?.description,
      CylinderVolume: v.objem ?? undefined,
      EnginePower: v.vykon1 ?? undefined,
      TotalWeigth: v.celkovaHmotnost ?? undefined,
      SeatsCount: v.pocetMistSezeni ?? undefined,
      FirstRegistrationDate: regDate,
      FirstRegistrationDateString: regDate ? format(regDate, 'd. M. yyyy') : undefined,
      BrokerMakeModelId: v.brokerMakeModelId ?? undefined,
      TechnicalCertificateNumber: v.cisloTechnickehoPrukazu ?? undefined,
      puvodVozidla: puvodData?.data?.find(x => x.id === v.puvod)?.description,
    }
  }, [ipl, druhData, palivoData])

  const minStartDate = useMemo(() => addDays(new Date(), 1), [])

  const defaultFormValues = useMemo<FormValues>(() => ({
    leasingContractNumber: '',
    stockType: Selection.After,
    stockStartDate: minStartDate,
    stockEndDate: addMonths(minStartDate, 36),
    vehicle: InitialVehicleValues,
  }), [minStartDate])

  const methods = useForm<FormValues>({
    defaultValues: defaultFormValues,
  })

  // Po kliknutí na "Nové pojištění" (z K1 sjednané smlouvy) – vyčistit formulář i případné override id (jako Step1 u aut)
  useEffect(() => {
    if (triggerNewInsurance) {
      setInsuranceParamsId(undefined)
      methods.reset(defaultFormValues)
    }
    clearTriggerNewInsurance()
  }, [triggerNewInsurance])

  useEffect(() => {
    if (!ipl) return

    if (isEdit) {
      const gp = ipl.generalParams
      methods.reset({
        leasingContractNumber: ipl.leasingContractNumber ?? '',
        stockType: gp?.paramsTypeId === InsuranceParamsType.StockBefore ? Selection.Berore : Selection.After,
        stockStartDate: gp?.insuranceStartDate ? new Date(gp.insuranceStartDate) : minStartDate,
        stockEndDate: gp?.insuranceEndDate ? new Date(gp.insuranceEndDate) : addMonths(minStartDate, 36),
        vehicle: ipl.vehicle ?? InitialVehicleValues,
        // keepErrors: zachovat serverové validační hlášky při dočtení dat po neúspěšném sjednání
      }, { keepErrors: true })
    } else {
      methods.reset({
        leasingContractNumber: ipl.leasingContractNumber ?? '',
        stockType: Selection.After,
        stockStartDate: minStartDate,
        stockEndDate: addMonths(minStartDate, 36),
        vehicle: ipl.vehicle ?? InitialVehicleValues,
      })
    }
  }, [ipl])

  const { mutateAsync: createStock, isPending } = useCreateStock()
  const { mutateAsync: checkActiveContracts, isPending: isCheckingContracts } = useCheckActiveContractsForStock()
  const { mutateAsync: checkStockVin, isPending: isCheckingVin } = useCheckStockVin()
  const [vinBlocked, setVinBlocked] = useState(false)
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null)
  const [stornoInfo, setStornoInfo] = useState<ManualStornoContractsInfo | null>(null)
  const { isOpen: isStornoOpen, onOpen: openStorno, onClose: closeStorno } = useDisclosure()
  // Po úspěšném založení: seznam ukončených smluv k zobrazení před redirectem na detail skladu
  const [cancelledContracts, setCancelledContracts] = useState<CancelledContractItem[]>([])
  const [stockDetailUrl, setStockDetailUrl] = useState<string>('')
  const { isOpen: isCancelledOpen, onOpen: openCancelled } = useDisclosure()
  const validationSummaryRef = useRef<HTMLDivElement>(null)

  const scrollToValidationSummary = () => {
    setTimeout(() => validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' }), 0)
  }

  const doCreateStock = async (values: FormValues, insuranceIdsToCancel?: number[]) => {
    try {
      const res = await createStock({
        ...values,
        origInsuranceParamsId: isFromPs ? origInsuranceParamsId : undefined,
        insuranceParamsId: insuranceParamsId ? insuranceParamsId : undefined,
        insuranceIdsToCancel: insuranceIdsToCancel?.length ? insuranceIdsToCancel : undefined,
      })

      if (res.success === false) {
        // Params už vytvořeny, ale sjednání selhalo – přepneme na editaci tohoto id (bez reloadu/URL změny),
        // zobrazíme serverové hlášky; další pokus už jen aktualizuje existující záznam.
        if (res.stockInsuranceParamsId)
          setInsuranceParamsId(res.stockInsuranceParamsId.toString())

        const msgs = res.errors?.length ? res.errors : ['Vytvoření skladového pojištění selhalo.']
        msgs.forEach((m, i) => methods.setError(`root.server${i}` as any, { message: m }))
        scrollToValidationSummary()

        return
      }

      const detailUrl = `/leasing/case/${res.stockInsuranceParamsId}/steps/complete/${res.stockInsurerOfferId}`

      // Pokud byly ukončeny nějaké smlouvy, zobrazíme je v modalu před přesměrováním na detail skladu
      if (res.cancelledContracts?.length) {
        setStockDetailUrl(detailUrl)
        setCancelledContracts(res.cancelledContracts)
        openCancelled()
        return
      }

      navigate(detailUrl)

    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data) {
        const data = err.response.data
        Object.keys(data).forEach((x: any) => {
          methods.setError(global.toCamelCase(x) as any, { message: data[x].join(' ') })
        })
      } else {
        methods.setError('root' as any, { message: 'Vytvoření skladového pojištění selhalo.' })
      }
      scrollToValidationSummary()
    }
  }

  const onSubmit = async (values: FormValues) => {
    const vin = values.vehicle?.vin?.trim()
    if (vin) {
      const { exists } = await checkStockVin(vin)
      setVinBlocked(exists)
      if (exists) {
        methods.setError('root' as any, { message: 'Na zadané VIN již existuje aktivní skladové pojištění.' })
        scrollToValidationSummary()
        return
      }
    }

    const info = await checkActiveContracts({
      leasingContractNumber: values.leasingContractNumber || undefined,
      vin: values.vehicle?.vin || undefined,
    })

    if (info?.contracts?.length) {
      setPendingValues(values)
      setStornoInfo(info)
      openStorno()
      return
    }

    await doCreateStock(values)
  }

  const handleConfirm = async (selectedIds: number[]) => {
    closeStorno()
    if (pendingValues) {
      setPendingValues(null)
      await doCreateStock(pendingValues, selectedIds)
    }
  }

  const needsData = isFromPs || isEdit
  const dataReady = !needsData || !!ipl

  // V edit režimu míří 2. krok na detail sjednané smlouvy (vč. offerId)
  const stockSteps = isEdit
    ? [
      stepsStock[0],
      { ...stepsStock[1], path: offerId ? `/leasing/case/${insuranceParamsId}/steps/complete/${offerId}` : stepsStock[1].path },
    ]
    : stepsStock

  return (
    <StepsProvider steps={stockSteps} currentStepId={1}>
      <SidebarProvider>
        <WizzardHeaderProvider>
          <FormProvider {...methods}>
            <form onSubmit={(e) => { methods.clearErrors(); void methods.handleSubmit(onSubmit, scrollToValidationSummary)(e) }}>
              <PageWrapper
                steps={<Steps />}
                sidebar={<Sidebar />}
                contentProps={{ sx: { pt: { base: 0, md: 0 } } }}
              >
                <Stack gap={8}>
                  {needsData && isLoading && !ipl && <Loading large />}

                  {dataReady && (
                    <fieldset disabled={isContracted} style={{ border: 0, margin: 0, padding: 0, minInlineSize: 'auto' }}>
                      <CreateStockBody
                        isFromPs={isFromPs}
                        psVehicle={psVehicle}
                        minStartDate={minStartDate}
                        isPending={isPending || isCheckingContracts || isCheckingVin}
                        isDisabled={vinBlocked || isContracted}
                        showSubmitButton={!isContracted}
                        onDuplicityChange={setVinBlocked}
                      />
                    </fieldset>
                  )}
                  <ErrorSummary errors={methods.formState.errors} ref={validationSummaryRef} />
                </Stack>
              </PageWrapper>
            </form>

            {stornoInfo && (
              <ActiveContractsModal
                contracts={stornoInfo.contracts}
                isOpen={isStornoOpen}
                onClose={closeStorno}
                onConfirm={handleConfirm}
              />
            )}

            <CancelledContractsModal
              contracts={cancelledContracts}
              isOpen={isCancelledOpen}
              onClose={() => navigate(stockDetailUrl)}
              stockDetailUrl={stockDetailUrl}
            />
          </FormProvider>
        </WizzardHeaderProvider>
      </SidebarProvider>
    </StepsProvider>
  )
}
